'use client';

import { useTurnkey } from '@turnkey/react-wallet-kit';
import { createInertTurnkeyDeps } from '../../../../tx-engine/turnkey-types.js';
// Phase 8/9 hardening (spec 056): the per-request diagnostic logs below are
// noisy in production. Gate the success-path traces to development; keep
// the catch-path errors UNGATED so production retains visibility on actual
// throws.
const isDev = typeof process !== 'undefined' && process.env?.NODE_ENV === 'development';
export function useTurnkeySubmissionDeps(logger) {
  const {
    authState,
    clientState,
    httpClient: client,
    session
  } = useTurnkey();
  const runtimeSnapshot = {
    clientState: clientState ?? 'unknown',
    authState: authState ?? 'unknown',
    hasHttpClient: !!client,
    hasSession: !!session
  };
  if (!client) {
    return createInertTurnkeyDeps(runtimeSnapshot);
  }
  // Do NOT pass `organizationId` — Turnkey's `httpClient` is bound to the
  // user's session sub-org. Passing the parent org id causes a 403
  // "could not find public key in organization" because the user's signing
  // key lives in their sub-org, not the parent.
  return {
    runtimeSnapshot,
    solSendTransaction: async params => {
      const t = params.transaction;
      if (isDev) {
        logger?.info('[turnkey-deps] solSendTransaction request', {
          signWith: t.signWith,
          caip2: t.caip2,
          sponsor: t.sponsor,
          recentBlockhash: t.recentBlockhash,
          unsignedTransactionPreview: `${t.unsignedTransaction.slice(0, 32)}…`,
          unsignedTransactionLen: t.unsignedTransaction.length
        });
      }
      try {
        // Pass the SolanaTransaction fields FLAT — the SDK does NOT
        // accept a `{ transaction: ... }` wrapper at this layer.
        const result = await client.solSendTransaction({
          unsignedTransaction: t.unsignedTransaction,
          signWith: t.signWith,
          caip2: t.caip2,
          ...(t.sponsor !== undefined ? {
            sponsor: t.sponsor
          } : {}),
          ...(t.recentBlockhash !== undefined ? {
            recentBlockhash: t.recentBlockhash
          } : {})
        });
        if (isDev) {
          logger?.info('[turnkey-deps] solSendTransaction response', {
            result: typeof result === 'string' ? result : JSON.stringify(result)
          });
        }
        return result.sendTransactionStatusId;
      } catch (err) {
        // UNGATED — production needs visibility on actual throws.
        logger?.error('[turnkey-deps] solSendTransaction threw', {
          message: err instanceof Error ? err.message.slice(0, 200) : String(err).slice(0, 200)
        });
        throw err;
      }
    },
    getSendTransactionStatus: async body => {
      try {
        const r = await client.getSendTransactionStatus(body);
        if (isDev) {
          logger?.info('[turnkey-deps] getSendTransactionStatus response', {
            statusId: `${body.sendTransactionStatusId.slice(0, 12)}…`,
            txStatus: r.txStatus,
            hasSig: r.solana?.signature !== undefined && r.solana.signature !== null,
            ...(r.error ? {
              error: JSON.stringify(r.error)
            } : {})
          });
        }
        return r;
      } catch (err_0) {
        // UNGATED — production needs visibility on actual throws.
        logger?.error('[turnkey-deps] getSendTransactionStatus threw', {
          message: err_0 instanceof Error ? err_0.message.slice(0, 200) : String(err_0).slice(0, 200)
        });
        throw err_0;
      }
    },
    getGasUsage: client.getGasUsage,
    getActivity: client.getActivity
  };
}